import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

/**
 * Age verification gate — shown once per browser before a user can
 * interact with any prize-eligible surface. Sweepstakes require
 * entrants to be 18+ (19+ in AL/NE) and US residents. We self-attest
 * here and record it in localStorage; the contest rules page is the
 * canonical legal text.
 *
 * Once passed, "fcb_age_verified" is stored with a timestamp. We
 * re-prompt after 180 days to keep the attestation reasonably fresh.
 *
 * NOT a legal-compliance wall by itself — it's an honest-person gate
 * plus a written record that the user attested. Pair with clear
 * Contest Rules and age fields on the entry form if you later
 * require stricter verification.
 */

const STORAGE_KEY = "fcb_age_verified";
const REVERIFY_AFTER_MS = 180 * 24 * 60 * 60 * 1000; // 180 days

type StoredAttestation = { ts: number; version: 1 };

function readAttestation(): StoredAttestation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttestation;
    if (!parsed?.ts) return null;
    if (Date.now() - parsed.ts > REVERIFY_AFTER_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAttestation() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), version: 1 }));
  } catch {
    /* private mode / storage full — ignore; user will reprompt next visit */
  }
}

export function AgeGate() {
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (readAttestation()) return;
    // Small delay so it doesn't flash during initial render
    const t = setTimeout(() => setOpen(true), 150);
    return () => clearTimeout(t);
  }, []);

  if (!open) return null;

  if (denied) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border p-8 text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <h2 className="font-display text-xl font-bold text-foreground">Sorry — you must be 18 or older</h2>
          <p className="text-sm text-muted-foreground">
            FC·Bracket prize contests are only open to US residents who are 18 or older (19+ in AL and NE).
          </p>
          <p className="text-xs text-muted-foreground">
            You can close this tab and come back when eligible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
    >
      <div className="max-w-md w-full bg-card border border-border">
        <div className="p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-primary" />
            <p className="text-[11px] font-semibold text-primary uppercase tracking-widest">FC · BRACKET</p>
          </div>

          <div className="space-y-3">
            <h2 id="age-gate-title" className="font-display text-2xl md:text-3xl font-black text-foreground leading-tight tracking-tight">
              CONFIRM YOU'RE 18 OR OLDER
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Prize contests on FC·Bracket are open only to US residents 18+ (19+ in AL and NE).
              By entering, you attest you meet that requirement.
            </p>
          </div>

          <div className="space-y-2 pt-2">
            <Button
              size="lg"
              onClick={() => {
                writeAttestation();
                setOpen(false);
              }}
              className="w-full font-display tracking-wider"
            >
              I AM 18 OR OLDER — CONTINUE
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setDenied(true)}
              className="w-full font-display tracking-wider"
            >
              I'M UNDER 18
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed pt-2 border-t border-border">
            No purchase necessary. See full{" "}
            <a href="/contest-rules" className="text-primary underline hover:no-underline">Contest Rules</a>
            {" "}for eligibility, odds, and prize details. Void where prohibited.
          </p>
        </div>
      </div>
    </div>
  );
}
